/**
 * Registered up front (messages.nav.ts), not with the lazy screens: the sidebar labels and badge, and the phone-number control that the
 * member, household, guest and search pages draw before any communications screen has loaded.
 */
export const communicationsNavEn = {
  "comms.phone.setupRequired": "Connect the club phone system and save your desk extension in Club Settings to use recorded calls and texts.",
  "comms.mail.title": "Email member",
  "comms.mail.hint": "Send from your connected work account and keep a copy in the member’s communication history.",
  "comms.mail.connectHint": "Connect your Microsoft work mailbox before sending. Your administrator must first link your work identity.",
  "comms.mail.connect": "Connect my Microsoft mailbox",
  "comms.mail.disconnect": "Disconnect my mailbox",
  "comms.mail.connectFailed": "Mailbox connection could not be started.",
  "comms.mail.send": "Send email",
  "comms.mail.required": "Enter a subject and message.",
  "comms.mail.to": "To",
  "comms.mail.from": "From",
  "comms.mail.subject": "Subject",
  "comms.mail.body": "Message",
  "comms.mail.permission": "Your role or license does not allow sending email.",
  "comms.mail.accepted": "Submitted to your mailbox and recorded here. Delivery is not confirmed.",
  "comms.mail.failed": "The mail service refused this message. It was recorded as failed.",
  "comms.mail.unknown": "The send outcome is uncertain. Check Sent Items before trying again; ClubCore will not resend it automatically.",

  "comms.module": "Phone and texts",
  "comms.nav.messages": "Phone messages",
  "comms.nav.log": "Call log",
  "comms.nav.settings": "Phone numbers",
  "comms.nav.texts": "Texts",
  "comms.texts.unreadBadge_one": "{count} unread text",
  "comms.texts.unreadBadge_other": "{count} unread texts",

  // A phone number anywhere in the staff app
  "comms.phone.options": "Call or text",
  "comms.phone.call": "Call",
  "comms.phone.text": "Text message",
  "comms.phone.calling": "Calling…",
  "comms.phone.ringing": "Your phone is ringing: pick up to connect to {number}.",
  "comms.phone.error.not_a_phone_number": "This number cannot be called.",
  "comms.phone.error.extension_required": "Save your desk extension first (My phone in Club settings), then try again.",
  "comms.phone.error.phone_not_configured": "The phone system is not set up. Ask a manager.",
  "comms.phone.error.generic": "The call could not be placed. Try again.",
  "comms.phone.dismiss": "Dismiss",
} as const;

/** Words for the communications screens: phone messages, the call and text log, texts, unknown callers, the member's timeline and phone-number settings. */
export const communicationsEn = {

  // What the log is made of
  "comms.channel.call": "Call",
  "comms.channel.text": "Text",
  "comms.channel.email": "Email",
  "comms.channel.note": "Note",
  "comms.direction.inbound": "Incoming",
  "comms.direction.outbound": "Outgoing",
  "comms.direction.internal": "Internal",
  "comms.status.answered": "Answered",
  "comms.status.missed": "Missed",
  "comms.status.voicemail": "Voicemail",
  "comms.status.sent": "Sent",
  "comms.status.delivered": "Delivered",
  "comms.status.failed": "Failed",
  "comms.status.received": "Received",
  "comms.status.logged": "Logged",
  "comms.match.exact_contact": "A member's saved number",
  "comms.match.staff_link": "Linked by staff",
  "comms.match.none": "No match",
  "comms.urgency.low": "Low",
  "comms.urgency.normal": "Normal",
  "comms.urgency.urgent": "Urgent",
  "comms.state.open": "Open",
  "comms.state.done": "Done",

  // Choosing a member
  "comms.picker.hint": "Type at least two letters of a name, or a member number.",
  "comms.picker.none": "No member matches that.",
  "comms.picker.change": "Change",

  // Phone messages
  "comms.messages.title": "Phone messages",
  "comms.messages.subtitle": "Messages taken for staff and departments, and who has dealt with them.",
  "comms.messages.new": "New phone message",
  "comms.messages.tab.mine": "For me",
  "comms.messages.tab.all": "All",
  "comms.messages.filter.state": "Show",
  "comms.messages.filter.open": "Open messages",
  "comms.messages.filter.done": "Finished messages",
  "comms.messages.filter.any": "Open and finished",
  "comms.messages.filter.recipient": "Left for",
  "comms.messages.filter.anyone": "Anyone",
  "comms.messages.none": "No phone messages yet",
  "comms.messages.none.hint": "Messages that are taken at the desk or the pro shop appear here.",
  "comms.messages.noMatches": "No messages match",
  "comms.messages.noMatches.hint": "Try showing open and finished messages, or choose a different person.",
  "comms.messages.col.when": "Taken",
  "comms.messages.col.from": "From",
  "comms.messages.col.about": "About",
  "comms.messages.col.for": "For",
  "comms.messages.col.message": "Message",
  "comms.messages.col.status": "Status",
  "comms.messages.col.actions": "Actions",
  "comms.messages.unknownCaller": "Unknown caller",
  "comms.messages.callback": "Call back: {number}",
  "comms.messages.doneOn": "Finished {when}",
  "comms.messages.markDone": "Mark done",
  "comms.messages.reopen": "Reopen",
  "comms.messages.actionFor": "{action}: message from {who}",
  "comms.messages.markedDone": "Message marked done.",
  "comms.messages.reopened": "Message reopened.",
  "comms.messages.form.callerName": "Caller's name",
  "comms.messages.form.callerNumber": "Caller's number",
  "comms.messages.form.callerNumber.hint": "Written any way; it is read in the club's country.",
  "comms.messages.form.callback": "Callback phone number (optional)",
  "comms.messages.form.callback.hint": "A different number to return the call, if needed.",
  "comms.messages.form.member": "Member this is about",
  "comms.messages.form.recipient": "Leave it for",
  "comms.messages.form.recipient.choose": "Choose a person or department",
  "comms.messages.form.recipient.staff": "Staff",
  "comms.messages.form.recipient.roles": "Departments",
  "comms.messages.form.recipientRequired": "Choose who the message is for.",
  "comms.messages.form.urgency": "Urgency",
  "comms.messages.form.notes": "Message",
  "comms.messages.form.notesRequired": "Write the message.",
  "comms.messages.form.submit": "Save message",
  "comms.messages.form.saved": "Phone message saved.",
  "apiError.not_a_phone_number": "That is not a phone number.",
  "apiError.recipient_unavailable": "That staff member or department cannot currently receive phone messages. Choose another recipient.",
  "apiError.not_your_message": "This message is for someone else.",

  // Call log
  "comms.log.title": "Call log",
  "comms.log.subtitle": "Calls and texts as the phone system reports them. The log itself is never edited; staff can say who a call was with.",
  "comms.log.tab.log": "Calls and texts",
  "comms.log.tab.unknown": "Unknown callers",
  "comms.log.filter.kind": "Kind",
  "comms.log.filter.anyKind": "Any kind",
  "comms.log.filter.direction": "Direction",
  "comms.log.filter.anyDirection": "Any direction",
  "comms.log.filter.number": "Phone number",
  "comms.log.filter.from": "From date",
  "comms.log.filter.to": "To date",
  "comms.log.filter.unmatched": "Only calls nobody is linked to",
  "comms.log.none": "Nothing in the call log yet",
  "comms.log.none.hint": "Calls and texts appear here once the phone system reports them.",
  "comms.log.noMatches": "No calls or texts match",
  "comms.log.noMatches.hint": "Widen the dates or clear a filter.",
  "comms.log.col.when": "When",
  "comms.log.col.kind": "Kind",
  "comms.log.col.number": "Number",
  "comms.log.col.member": "Member",
  "comms.log.col.duration": "Duration",
  "comms.log.col.extension": "Extension",
  "comms.log.unknown": "Unknown",
  "comms.log.householdOnly": "{name} household",
  "comms.log.detail.callerName": "Caller ID name",
  "comms.log.detail.title": "Call details",
  "comms.log.detail.status": "Result",
  "comms.log.detail.asGiven": "Number as reported",
  "comms.log.detail.staff": "Staff member",
  "comms.log.detail.subject": "Subject",
  "comms.log.detail.body": "Text",
  "comms.log.detail.summary": "Summary",
  "comms.log.detail.recording": "Recording",
  "comms.log.detail.openRecording": "Open the recording",
  "comms.log.detail.matchedBy": "Matched by",
  "comms.log.candidates": "Members who share this number",
  "comms.log.candidates.count_one": "{count} member shares this number.",
  "comms.log.candidates.count_other": "{count} members share this number.",
  "comms.log.link": "Link",
  "comms.log.linkTo": "Link this call to {name}",
  "comms.log.linkHeading": "Who was this?",
  "comms.log.changeLink": "Change who this was",
  "comms.log.linkMember": "Member",
  "comms.log.linkToMember": "Link to this member",
  "comms.log.removeLink": "Remove the link",
  "comms.log.linked": "Call linked.",
  "comms.log.unlinked": "Link removed.",

  // Unknown callers
  "comms.unknown.none": "No unknown callers",
  "comms.unknown.none.hint": "Every number that called or texted matches a member.",
  "comms.unknown.col.entries": "Calls and texts",
  "comms.unknown.col.channels": "Kinds",
  "comms.unknown.col.first": "First seen",
  "comms.unknown.col.last": "Last seen",
  "comms.unknown.link": "Link to member",
  "comms.unknown.linkFor": "Link {number} to a member",
  "comms.unknown.linkTitle": "Who is {number}?",
  "comms.unknown.linkBody": "Their earlier unmatched calls and texts are linked, and future ones match this member.",
  "comms.unknown.member": "Member",
  "comms.unknown.memberRequired": "Choose a member.",
  "comms.unknown.addContact": "Also add this number to the member's contacts",
  "comms.unknown.addContact.hint": "so it is on their record.",
  "comms.unknown.linkSubmit": "Link number",
  "comms.unknown.linked_one": "{number} linked to {name}; {count} earlier call or text filled in.",
  "comms.unknown.linked_other": "{number} linked to {name}; {count} earlier calls or texts filled in.",

  // Member page
  "comms.timeline.title": "Communication history",
  "comms.timeline.none": "No communications are linked to this person yet.",
  "comms.timeline.more": "Show more",

  // Phone-number settings
  "comms.settings.title": "Phone numbers",
  "comms.settings.subtitle": "How the phone system's numbers are read.",
  "comms.settings.card": "Country for phone numbers",
  "comms.settings.region": "Country",
  "comms.settings.region.hint": "Numbers written without a country code, such as 843 555 0142, are read as numbers in this country.",
  "comms.settings.saved": "Phone number settings saved.",
  "comms.region.US": "United States",
  "comms.region.CA": "Canada",
  "comms.region.BS": "Bahamas",
  "comms.region.BM": "Bermuda",
  "comms.region.GB": "United Kingdom",
  "comms.region.IE": "Ireland",
  "comms.region.AU": "Australia",
  "comms.region.NZ": "New Zealand",
  "comms.region.MX": "Mexico",

  // Replying to HELP
  "comms.settings.help.card": "Reply to HELP",
  "comms.settings.help.label": "Text sent to anyone who texts HELP",
  "comms.settings.help.hint": "Leave empty to send: {text}",
  "comms.settings.help.saved": "HELP reply saved.",

  // Writing a text
  "comms.compose.title": "Text {name}",
  "comms.compose.from": "Sent from the club's number {number}.",
  "comms.compose.message": "Message",
  "comms.compose.send": "Send",
  "comms.compose.sent": "Text sent to {number}.",
  "comms.compose.parts_one": "{characters} characters · 1 part",
  "comms.compose.parts_other": "{characters} characters · {count} parts",
  "comms.compose.unicode": "Accents outside the standard set or emoji make each part shorter (70 characters).",
  "comms.compose.tooLong": "Too long: a text can be at most {count} parts.",
  "comms.compose.optedOut": "This number replied STOP on {date}. The club cannot text it until it texts START.",
  "comms.compose.optedOutUndated": "This number replied STOP. The club cannot text it until it texts START.",
  "comms.compose.historyHidden": "You cannot see earlier texts with this number.",
  "comms.compose.openConversation": "Open the conversation",
  "comms.compose.error.recipient_opted_out": "This number replied STOP, so it cannot be texted.",
  "comms.compose.error.sms_rate_limited": "You have sent many texts in the last few minutes. Wait a little and try again.",
  "comms.compose.error.sms_not_available": "The club's phone system cannot send texts yet. Ask a manager.",
  "comms.compose.error.text_too_long": "The text is too long.",
  "comms.compose.error.empty_text": "Write the text first.",
  "comms.compose.error.not_a_phone_number": "This number cannot receive texts.",
  "comms.compose.error.generic": "The text was not sent. Try again.",

  // A conversation
  "comms.thread.label": "Texts with {name}",
  "comms.thread.none": "No texts with this number yet.",
  "comms.thread.older": "Show earlier texts",
  "comms.thread.incoming": "From them",
  "comms.thread.outgoing": "From the club",
  "comms.thread.sentBy": "Sent by {name}",
  "comms.thread.automatic": "Automatic reply to HELP",
  "comms.thread.notSent": "Not sent: {reason}",
  "comms.thread.errorCode": "Carrier code {code}",

  // The texts inbox
  "comms.texts.title": "Texts",
  "comms.texts.subtitle": "Text conversations with the club's number, newest first.",
  "comms.texts.list": "Conversations",
  "comms.texts.unreadOnly": "Only unread",
  "comms.texts.none": "No texts yet",
  "comms.texts.none.hint": "Texts to and from the club's number appear here.",
  "comms.texts.noneUnread": "No unread texts",
  "comms.texts.noneUnread.hint": "Every conversation has been read.",
  "comms.texts.unread_one": "{count} unread",
  "comms.texts.unread_other": "{count} unread",
  "comms.texts.optedOut": "Opted out",
  "comms.texts.unknown": "Not a known number",
  "comms.texts.you": "You: {text}",
  "comms.texts.pick": "Choose a conversation to read it.",
  "comms.texts.back": "All conversations",
  "comms.texts.openMember": "Open member",
  "comms.texts.cannotSend": "You can read these texts but not send one.",
  "comms.messages.finished": "Finished",
  "comms.messages.general": "General message",
  "comms.messages.delete": "Delete",
  "comms.messages.deleteFor": "Delete message from {who}",
  "comms.messages.deleteTitle": "Delete phone message?",
  "comms.messages.deleteConfirm": "This message will be removed from the lists. Its audit history is retained, and a system administrator can recover it.",
  "comms.messages.deleted": "Phone message deleted.",
  "comms.messages.callbackDue": "Callback due: {when}",
  "comms.messages.form.callbackDate": "Callback date (optional)",
  "comms.messages.form.callbackTime": "Callback time (optional)",
  "comms.messages.form.callbackZone": "Callback date and time use the club time zone: {zone}. Leave both blank if no callback is scheduled.",
  "comms.messages.form.callbackInvalid": "Enter both a valid date and time. Choose a time outside a skipped or repeated daylight-saving hour.",
  "comms.messages.form.about": "Message is about",
  "comms.messages.form.general": "General message / nonmember",
  "comms.messages.form.aboutMember": "A member (optional link)",
  "comms.messages.form.generalHint": "Messages may be about anyone; a member link is optional.",
} as const;

export type CommunicationsMessages = typeof communicationsEn & typeof communicationsNavEn;
